import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Check, X, Sparkles, Clock, ShieldCheck, Zap, Trophy, Gift, ChevronRight, AlertCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const FEATURES = [
  { icon: Trophy, title: "Simulacros oficiales históricos", desc: "Acceso a todos los exámenes oficiales de convocatorias anteriores" },
  { icon: Sparkles, title: "Estadísticas avanzadas", desc: "Análisis profundo, predicciones de nota y comparativas nacionales" },
  { icon: Zap, title: "Sin límites diarios", desc: "Tests ilimitados, preguntas falladas ilimitadas, retos diarios extra" },
  { icon: ShieldCheck, title: "Soporte prioritario", desc: "Respuesta en menos de 24h y acceso anticipado a novedades" },
];

const PLAN_ORDER = ["monthly", "quarterly", "semiannual", "annual"];
const PLAN_TAGS = {
  monthly: null,
  quarterly: "Popular",
  semiannual: "Ahorras 41%",
  annual: "Mejor precio",
};
const PLAN_HIGHLIGHT = {
  annual: true,
};

export default function Premium() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [providers, setProviders] = useState({ stripe: true, paypal: false });
  const [trialDays, setTrialDays] = useState(7);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'trial' | 'stripe:plan' | 'paypal:plan'

  const loadAll = async () => {
    setLoading(true);
    try {
      const [plansRes, statusRes] = await Promise.all([
        api.get("/premium/plans"),
        user ? api.get("/premium/status") : Promise.resolve({ data: null }),
      ]);
      setPlans(plansRes.data.plans || []);
      setProviders(plansRes.data.providers || { stripe: true, paypal: false });
      setTrialDays(plansRes.data.trial_days || 7);
      setStatus(statusRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    if (location.search.includes("canceled=1")) {
      toast.info("Has cancelado el pago. Puedes intentarlo cuando quieras.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedPlans = PLAN_ORDER
    .map((id) => plans.find((p) => p.id === id))
    .filter(Boolean);

  const handleTrial = async () => {
    if (!user) { navigate("/register?redirect=/premium"); return; }
    setBusy("trial");
    try {
      await api.post("/premium/trial/start");
      toast.success(`¡Prueba gratuita activada! Tienes ${trialDays} días de Premium.`);
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo activar la prueba");
    } finally {
      setBusy(null);
    }
  };

  const handleStripe = async (planId) => {
    if (!user) { navigate(`/register?redirect=/premium`); return; }
    setBusy(`stripe:${planId}`);
    try {
      const { data } = await api.post("/premium/checkout/stripe", {
        plan: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error iniciando pago Stripe");
      setBusy(null);
    }
  };

  const handlePayPal = async (planId) => {
    if (!user) { navigate(`/register?redirect=/premium`); return; }
    setBusy(`paypal:${planId}`);
    try {
      const { data } = await api.post("/premium/paypal/create-order", { plan: planId });
      if (data.approve_url) {
        window.location.href = data.approve_url;
      } else {
        toast.error("PayPal no devolvió URL de aprobación");
        setBusy(null);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error iniciando pago PayPal");
      setBusy(null);
    }
  };

  const isPremium = status?.is_premium;
  const isTrial = status?.is_trial;

  return (
    <div className="min-h-screen bg-ink relative overflow-hidden">
      <SEO
        title="Premium · TestOpoGuardiaCivil"
        description="Plan Premium con simulacros oficiales históricos, estadísticas avanzadas y tests ilimitados. Desde 5,50€/mes."
      />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />
      <div className="fixed inset-0 bg-dot-grid pointer-events-none opacity-40" />
      <div className="relative">
        <Header />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gcgold/10 border border-gcgold/30 text-gcgold font-mono text-xs uppercase tracking-[0.22em] mb-5">
              <Crown className="h-3.5 w-3.5" /> Premium
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white tracking-tighter leading-[0.95]">
              La <span className="text-gcgold">ventaja desleal</span><br />que te lleva a la plaza.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
              Desbloquea simulacros oficiales históricos, estadísticas avanzadas y tests ilimitados. Cancela cuando quieras.
            </p>
          </motion.div>

          {/* Current status banner */}
          {!loading && isPremium && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto mb-12 p-5 rounded-2xl border border-gcgold/30 bg-gradient-to-r from-gcgold/10 to-transparent"
              data-testid="premium-active-banner"
            >
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-gcgold/20 flex items-center justify-center">
                  <Crown className="h-6 w-6 text-gcgold" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-lg font-bold text-white">
                    {isTrial ? "Prueba gratuita activa" : "Premium activo"}
                  </div>
                  <div className="text-sm text-slate-400">
                    {status.days_left} días restantes · vence el {new Date(status.expires_at).toLocaleDateString("es-ES")}
                  </div>
                </div>
                {isTrial && (
                  <span className="px-3 py-1 rounded-full bg-gcgreen/20 text-gcgreen text-xs font-bold uppercase">Trial</span>
                )}
              </div>
            </motion.div>
          )}

          {/* Trial CTA */}
          {!loading && !isPremium && user && !status?.trial_used && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto mb-12 p-6 rounded-2xl border-2 border-dashed border-gcgreen/40 bg-gcgreen/5"
              data-testid="trial-cta-card"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-gcgreen/20 flex items-center justify-center flex-shrink-0">
                  <Gift className="h-6 w-6 text-gcgreen-light" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-xl font-bold text-white">
                    Prueba {trialDays} días gratis
                  </div>
                  <div className="text-sm text-slate-400 mt-1">
                    Sin tarjeta. Sin compromiso. Se cancela automáticamente al terminar.
                  </div>
                </div>
                <Button
                  onClick={handleTrial}
                  disabled={busy === "trial"}
                  className="w-full md:w-auto bg-gcgreen hover:bg-gcgreen-light text-white font-bold rounded-xl shadow-glow"
                  data-testid="start-trial-button"
                >
                  {busy === "trial" ? "Activando..." : "Empezar prueba gratis"}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Plans grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16" data-testid="plans-grid">
            {sortedPlans.map((plan, idx) => {
              const tag = PLAN_TAGS[plan.id];
              const highlight = PLAN_HIGHLIGHT[plan.id];
              const stripeBusy = busy === `stripe:${plan.id}`;
              const paypalBusy = busy === `paypal:${plan.id}`;
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.06 }}
                  className={`relative rounded-2xl p-6 flex flex-col ${
                    highlight
                      ? "bg-gradient-to-b from-gcgold/15 via-ink-800 to-ink-800 border-2 border-gcgold/50 shadow-[0_0_60px_-15px_rgba(212,175,55,0.4)]"
                      : "bg-ink-800 border border-white/5 hover:border-white/10"
                  } transition-all`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {tag && (
                    <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold ${
                      highlight ? "bg-gcgold text-ink" : "bg-gcgreen text-white"
                    }`}>
                      {tag}
                    </div>
                  )}
                  <div className="text-xs uppercase tracking-[0.22em] text-gcgold font-mono mb-2">
                    {plan.label}
                  </div>
                  <div className="font-display text-2xl font-bold text-white mb-1">
                    {plan.name}
                  </div>
                  <div className="flex items-baseline gap-1 mt-3">
                    <span className="font-display text-5xl font-extrabold text-white tracking-tighter tabular-nums">
                      {plan.price.toFixed(2).replace(".", ",")}
                    </span>
                    <span className="text-2xl text-slate-400 font-bold">€</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1.5 font-mono">
                    Equivale a <span className="text-slate-300">{plan.monthly_equiv.toFixed(2).replace(".", ",")}€/mes</span>
                  </div>
                  <div className="flex-1 mt-5 space-y-2 text-sm text-slate-300">
                    <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-gcgreen-light flex-shrink-0" /> Simulacros oficiales</div>
                    <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-gcgreen-light flex-shrink-0" /> Estadísticas avanzadas</div>
                    <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-gcgreen-light flex-shrink-0" /> Tests ilimitados</div>
                    <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-gcgreen-light flex-shrink-0" /> Soporte prioritario</div>
                  </div>
                  <div className="mt-6 space-y-2">
                    <Button
                      onClick={() => handleStripe(plan.id)}
                      disabled={!!busy || isPremium}
                      className={`w-full font-bold rounded-xl ${
                        highlight
                          ? "bg-gcgold hover:bg-gcgold-light text-ink"
                          : "bg-white text-ink hover:bg-slate-100"
                      }`}
                      data-testid={`pay-stripe-${plan.id}`}
                    >
                      {stripeBusy ? "Cargando..." : "Pagar con tarjeta"}
                    </Button>
                    {providers.paypal ? (
                      <Button
                        onClick={() => handlePayPal(plan.id)}
                        disabled={!!busy || isPremium}
                        className="w-full bg-[#0070ba] hover:bg-[#003087] text-white font-bold rounded-xl"
                        data-testid={`pay-paypal-${plan.id}`}
                      >
                        {paypalBusy ? "Cargando..." : "PayPal"}
                      </Button>
                    ) : (
                      <div className="text-center text-[10px] uppercase tracking-[0.2em] text-slate-600 font-mono py-1">
                        PayPal · próximamente
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Features detailed */}
          <section className="max-w-5xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white text-center tracking-tighter mb-10">
              Lo que <span className="text-gcgold">desbloqueas</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="bg-ink-800 border border-white/5 rounded-2xl p-5 hover:border-gcgold/30 transition-colors" data-testid={`feature-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-xl bg-gcgold/15 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-gcgold" />
                      </div>
                      <div>
                        <div className="font-display text-lg font-bold text-white">{f.title}</div>
                        <div className="text-sm text-slate-400 mt-1">{f.desc}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-3xl mx-auto mt-20">
            <h2 className="font-display text-3xl font-extrabold text-white text-center mb-8 tracking-tighter">Preguntas frecuentes</h2>
            <div className="space-y-3 text-sm">
              {[
                ["¿Necesito tarjeta para la prueba gratuita?", "No. La prueba de 7 días es totalmente gratis y se cancela sola al terminar. No te cobramos nada hasta que decidas suscribirte."],
                ["¿Cómo cancelo?", "Puedes cancelar cuando quieras desde tu perfil. Mantienes el acceso Premium hasta la fecha de vencimiento del periodo pagado."],
                ["¿Qué métodos de pago aceptáis?", "Tarjeta de crédito/débito vía Stripe y PayPal. Todos los pagos son seguros y procesados por entidades certificadas PCI-DSS."],
                ["¿Hay reembolso si no estoy satisfecho?", "Tras los 7 días gratuitos no hay reembolso, pero puedes cancelar la renovación en cualquier momento."],
              ].map(([q, a], i) => (
                <details key={i} className="group bg-ink-800 border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors">
                  <summary className="cursor-pointer font-bold text-white flex items-center justify-between">
                    {q}
                    <ChevronRight className="h-4 w-4 text-slate-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="mt-3 text-slate-400">{a}</p>
                </details>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </div>
  );
}
